import { useTranslation } from 'react-i18next';
import { COMMERCIAL_PRICING, COMMERCIAL_SITE } from '@connexio/shared/constants';
import {
  PublicMarketingShell,
  type MarketingNavSection,
} from '@/components/public/PublicMarketingShell';
import { Button } from '@/components/ui/button';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useLocale } from '@/hooks/useLocale';
import { useCommercialConfig } from './publicApi';
import { Hero } from './components/Hero';
import { TrustStrip } from './components/TrustStrip';
import { ValueProps } from './components/ValueProps';
import { PricingCard } from './components/PricingCard';
import { EnterpriseForm } from './components/EnterpriseForm';
import { Faq } from './components/Faq';
import { DevelopmentStatus } from './components/DevelopmentStatus';
import { Section } from './components/Section';
import { MarketingFooter } from './components/MarketingFooter';
import { PrimaryCta } from './components/PrimaryCta';
import { ContactEmailNote } from './components/ContactEmailNote';
import { marketingContactHref } from './contactHref';
import styles from './HomePage.module.css';

/** Shared with the static tags in `client/index.html`, which crawlers read without JS. */
const MARKETING_OG_IMAGE_PATH = '/showcase/og-product.png';

function ClosingCta({ signupEnabled }: { signupEnabled: boolean }) {
  const { t } = useTranslation('marketing');
  const days = COMMERCIAL_PRICING.trialDays;

  return (
    <Section id="get-started" tone="dark" labelledBy="closing-title" className={styles.section}>
      <div className={styles.box}>
        <h2
          id="closing-title"
          className={styles.title}
        >
          {signupEnabled ? t('closing.title', { days }) : t('closing.titleNoTrial')}
        </h2>
        <p className={styles.subtitle}>
          {signupEnabled ? t('closing.subtitle') : t('closing.subtitleNoTrial')}
        </p>
        <div className={styles.stack}>
          <PrimaryCta signupEnabled={signupEnabled} />
          {/* Secondary: talk to us next to the trial; when the trial button is itself
              "talk to us", the price is the next thing a new visitor needs. */}
          {signupEnabled ? (
            <Button asChild variant="outline" size="xl">
              <a href={marketingContactHref()}>{t('cta.contact')}</a>
            </Button>
          ) : (
            <Button asChild variant="outline" size="xl">
              <a href="#pricing">{t('closing.seePrice')}</a>
            </Button>
          )}
        </div>
        <ContactEmailNote className={styles.contactEmailNote} />
      </div>
    </Section>
  );
}

export function HomePage() {
  const { t } = useTranslation('marketing');
  const { locale } = useLocale();
  const { data: config } = useCommercialConfig();
  const signupEnabled = config?.signupEnabled === true;

  useDocumentMeta({
    title: t('meta.title'),
    // The static description in index.html promises the trial; drop that when sign-up is closed.
    description: signupEnabled ? t('meta.description') : t('meta.descriptionNoTrial'),
    canonicalPath: `${COMMERCIAL_SITE.canonicalOrigin}/`,
    lang: locale,
    ogImagePath: MARKETING_OG_IMAGE_PATH,
    ogImageAlt: t('meta.ogImageAlt'),
    // No hreflang alternates: sv and en share this one URL (the language switches in
    // the client), and alternates that all point at the same URL are invalid.
  });

  // Short labels, not the section headings: the headings are full sentences and
  // four of them do not fit one header row in either locale.
  const navSections: MarketingNavSection[] = [
    { id: 'product', label: t('nav.product') },
    { id: 'pricing', label: t('nav.pricing') },
    { id: 'enterprise', label: t('nav.enterprise') },
    { id: 'faq', label: t('nav.faq') },
  ];

  // Short label: on a phone the header holds only the logo mark, the language toggle
  // and this button (see `compactActionsOnPhone`); sign in sits under the hero buttons.
  const headerActions = (
    <PrimaryCta signupEnabled={signupEnabled} label="short" size="sm" className={styles.primaryCta} />
  );

  return (
    <PublicMarketingShell
      brandTo="/"
      brandTagline={t('nav.brandTagline')}
      navLabel={t('nav.label')}
      sections={navSections}
      actions={headerActions}
      compactActionsOnPhone
      footer={<MarketingFooter />}
      fullBleed
      inlineNav
      surfaceClassName="marketing-surface"
    >
      <Hero signupEnabled={signupEnabled} />
      <TrustStrip signupEnabled={signupEnabled} />
      <ValueProps signupEnabled={signupEnabled} />
      <PricingCard
        pricing={config?.pricing}
        trialDays={config?.trialDays}
        signupEnabled={signupEnabled}
      />
      <DevelopmentStatus />
      <EnterpriseForm />
      <Faq signupEnabled={signupEnabled} />
      <ClosingCta signupEnabled={signupEnabled} />
    </PublicMarketingShell>
  );
}

export default HomePage;
